<template>
  <div id="app">
    <!-- 顶部导航栏 -->
    <el-header class="header" height="85px">
      <div class="header-container">
        <div class="left-section">
          <div class="logo">
            <router-link to="/">
              <h1>AiVitaHelper</h1>
            </router-link>
          </div>
          
          <!-- 桌面端导航菜单 -->
          <div class="nav-menu desktop-menu">
            <router-link to="/" class="nav-item">首页</router-link>
            <router-link to="/resume" class="nav-item">简历优化</router-link>
            <router-link to="/resources/simple" class="nav-item">资源管理</router-link>
            <router-link to="/xiaoguo-square" class="nav-item">小果广场</router-link>
            <router-link to="/about" class="nav-item">关于</router-link>
          </div>
        </div>
        
        <div class="right-section">
          <div class="auth-buttons" v-if="!isAuthenticated">
            <el-button size="small" class="login-btn" @click="navigateTo('login')">登录</el-button>
            <el-button size="small" type="primary" class="register-btn" @click="navigateTo('register')">注册</el-button>
          </div>
          <template v-else>
            <div class="user-section">
              <div 
                class="user-info" 
                @mouseenter="showTooltip = true"
                @mouseleave="showTooltip = false"
                @click="navigateTo('user-profile')"
              >
                <img 
                  :src="avatarUrl" 
                  :alt="currentUser?.username || '用户头像'" 
                  class="user-avatar"
                />
                <VipBadge
                  v-if="currentUser?.vip"
                  :level="currentUser.vip"
                  :expires-at="currentUser.vipExpiresAt"
                  type="mini"
                  class="avatar-vip-badge"
                />
              </div>
              <div 
                v-show="showTooltip" 
                class="user-tooltip"
                @mouseenter="showTooltip = true"
                @mouseleave="showTooltip = false"
              >
                <div class="tooltip-content" @click="navigateTo('user-profile')">
                  <div class="tooltip-header">
                    <img 
                      :src="avatarUrl" 
                      :alt="currentUser?.username || '用户头像'" 
                      class="tooltip-avatar"
                    />
                    <div class="tooltip-user-info">
                      <div class="tooltip-name-wrapper">
                        <span class="tooltip-name">{{ currentUser?.username || '未知用户' }}</span>
                        <VipBadge
                          v-if="currentUser?.vip"
                          :level="currentUser.vip"
                          :expires-at="currentUser.vipExpiresAt"
                          type="mini"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="tooltip-details">
                    <div class="detail-item">
                      <span class="label">账号：</span>
                      <span class="value">{{ currentUser?.account }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">邮箱：</span>
                      <span class="value">{{ currentUser?.email }}</span>
                    </div>
                  </div>
                </div>
                <el-divider></el-divider>
                <div class="tooltip-actions">
                  <button class="action-btn" @click.stop="navigateTo('user-profile', '#settings')">设置</button>
                  <button 
                    class="action-btn" 
                    @click.stop="showVipRedeemDialog"
                  >
                    {{ currentUser?.vip ? 'VIP兑换码' : 'VIP兑换码' }}
                  </button>
                  <button class="action-btn logout-btn" @click.stop="handleLogout">退出登录</button>
                </div>
              </div>
            </div>
            <NotificationIcon />
          </template>
          
          <!-- 移动端菜单按钮 -->
          <div class="mobile-menu-btn" @click.stop="toggleMobileMenu">
            <div class="menu-icon-container">
            <el-icon v-if="!mobileMenuVisible"><Menu /></el-icon>
            <el-icon v-else><Close /></el-icon>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 移动端导航菜单 -->
      <div class="mobile-menu" :class="{ 'mobile-menu-visible': mobileMenuVisible }">
        <router-link to="/" class="mobile-nav-item" @click="closeMobileMenu">首页</router-link>
        <router-link to="/resume" class="mobile-nav-item" @click="closeMobileMenu">简历优化</router-link>
        <router-link to="/resources/simple" class="mobile-nav-item" @click="closeMobileMenu">资源管理</router-link>
        <router-link to="/xiaoguo-square" class="mobile-nav-item" @click="closeMobileMenu">小果广场</router-link>
        <router-link to="/about" class="mobile-nav-item" @click="closeMobileMenu">关于</router-link>
        <div v-if="!isAuthenticated" class="mobile-auth-buttons">
          <el-button size="small" class="login-btn" @click="navigateTo('login')">登录</el-button>
          <el-button size="small" type="primary" class="register-btn" @click="navigateTo('register')">注册</el-button>
        </div>
      </div>
    </el-header>
    
    <!-- 主要内容区域 -->
    <el-main class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </el-main>
    
    <!-- 页脚 -->
    <el-footer class="footer">
      <div class="footer-content">
        <div class="copyright">
          &copy; {{ currentYear }} AI简历助手. 保留所有权利.
          <a href="https://beian.miit.gov.cn" target="_blank" class="beian-link">京ICP备2025105454号-2 </a>
          <img src="./assets/备案图标.png" alt="Icon" width="10" />
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=11010802045582" rel="noreferrer" target="_blank" class="beian-link">京公网安备11010802045582号</a>
        </div>
      </div>
    </el-footer>
    
    <!-- VIP兑换对话框 -->
    <VipRedeemDialog v-model:visible="vipRedeemDialogVisible" @redeemed="handleVipRedeemed" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { Menu, Close, User, Setting, SwitchButton } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/modules/auth';
import { useUserStore } from '@/store/modules/user';
import { ElMessage } from 'element-plus';
import defaultAvatar from '@/assets/defualt.jpg';
import VipBadge from '@/components/VipBadge.vue';
import NotificationIcon from '@/components/NotificationIcon.vue';
import VipRedeemDialog from '@/components/VipRedeemDialog.vue';
import api from '@/services/api';
import { resourceApi } from '@/services/api';
import { config } from '@/config/config';

// 添加基础URL配置 - 使用config
const API_BASE_URL = config.apiBaseUrl;

export default defineComponent({
  name: 'App',
  components: {
    Menu,
    Close,
    User,
    Setting,
    SwitchButton,
    VipBadge,
    NotificationIcon,
    VipRedeemDialog
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const mobileMenuVisible = ref(false);
    const showTooltip = ref(false);
    const currentUser = computed(() => userStore.userInfo);
    const isAuthenticated = computed(() => authStore.isAuthenticated);
    const vipRedeemDialogVisible = ref(false);
    const isMobile = computed(() => window.innerWidth <= 768);
    
    const avatarUrlRef = ref<string>(defaultAvatar);

    // 处理头像URL
    const loadAvatar = async () => {
      if (!currentUser.value?.avatarUrl) {
        avatarUrlRef.value = defaultAvatar;
        return;
      }
      
      // 如果已经是完整URL则直接返回
      if (currentUser.value.avatarUrl.startsWith('http')) {
        avatarUrlRef.value = currentUser.value.avatarUrl;
        return;
      }

      try {
        // 获取图片数据
        const response = await resourceApi.get(currentUser.value.avatarUrl, {
          responseType: 'blob'
        });
        
        // 将blob转换为base64
        const reader = new FileReader();
        reader.onloadend = () => {
          avatarUrlRef.value = reader.result as string;
        };
        reader.readAsDataURL(response.data);
      } catch (error) {
        console.error('加载头像失败:', error);
        avatarUrlRef.value = defaultAvatar;
      }
    };

    // 监听用户信息变化
    watch(() => currentUser.value?.avatarUrl, (newAvatarUrl) => {
      if (newAvatarUrl) {
        loadAvatar();
      }
    }, { immediate: true });
    
    const toggleMobileMenu = () => {
      mobileMenuVisible.value = !mobileMenuVisible.value;
    };
    
    const closeMobileMenu = () => {
      mobileMenuVisible.value = false;
    };
    
    const navigateTo = (route: string, hash?: string) => {
      // 如果当前已经在user-profile页面并且没有传入hash，则替换当前路由以清除hash
      if (router.currentRoute.value.name === 'user-profile' && !hash) {
        router.replace(`/${route}`);
      } else {
        router.push(hash ? `/${route}${hash}` : `/${route}`);
      }
      closeMobileMenu();
    };

    // 显示VIP兑换对话框
    const showVipRedeemDialog = () => {
      vipRedeemDialogVisible.value = true;
      closeMobileMenu();
    };

    // 处理VIP兑换成功
    const handleVipRedeemed = () => {
      // 可以在这里添加额外的处理，如更新状态等
    };

    const handleLogout = async () => {
      try {
        await authStore.logout();
        userStore.clearUserInfo();
        ElMessage.success('退出登录成功');
        router.push('/login');
      } catch (error) {
        ElMessage.error('退出登录失败');
      }
    };
    
    const currentYear = computed(() => new Date().getFullYear());
    
    onMounted(() => {
      if (authStore.isAuthenticated) {
        userStore.fetchUserInfo();
      }
      
      // 监听窗口大小变化
      window.addEventListener('resize', () => {
        if (window.innerWidth > 768) {
          mobileMenuVisible.value = false;
        }
      });
      
      // 点击页面任何位置关闭用户菜单和移动菜单
      document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const avatarContainer = document.querySelector('.avatar-container');
        const mobileMenuBtn = document.querySelector('.mobile-menu-btn');
        
        if (avatarContainer && !avatarContainer.contains(target)) {
          showTooltip.value = false;
        }
        
        if (mobileMenuBtn && !mobileMenuBtn.contains(target) && 
            !document.querySelector('.mobile-menu')?.contains(target)) {
          mobileMenuVisible.value = false;
        }
      });
    });

    watch(() => authStore.isAuthenticated, (newValue) => {
      if (newValue) {
        userStore.fetchUserInfo();
      }
    });
    
    return {
      mobileMenuVisible,
      toggleMobileMenu,
      closeMobileMenu,
      navigateTo,
      currentYear,
      currentUser,
      isAuthenticated,
      showTooltip,
      handleLogout,
      defaultAvatar,
      avatarUrl: avatarUrlRef,
      vipRedeemDialogVisible,
      showVipRedeemDialog,
      handleVipRedeemed,
      isMobile
    };
  }
});
</script>

<style lang="scss">
// 全局应用样式优化 - 增加前置样式提高优先级
body {
  margin: 0;
  padding: 0;
  min-height: 100%;
  width: 100%;
}

// 全局样式重置
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  line-height: 1.5;
  background-color: #f5f5f7;
}

a {
  text-decoration: none;
  color: inherit;
}

// 应用容器
#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

// 头部导航样式
.header {
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
  height: 85px;
  padding: 0;
  
  .header-container {
    width: 95%;
    margin: 0 auto;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .left-section {
    display: flex;
    align-items: center;
  }
  
  .logo {
    margin-right: 60px;
    
    h1 {
      font-size: 1.8rem;
      font-weight: 600;
      color: #222;
      margin: 0;
      padding: 10px 0;
    }
  }
  
  .nav-menu {
    display: flex;
    gap: 40px;
    
    .nav-item {
      font-size: 1.1rem;
      font-weight: 500;
      color: #555;
      transition: color 0.2s;
      padding: 12px 0;
      
      &:hover, &.router-link-active {
        color: #222;
      }
    }
  }
  
  .right-section {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px; /* 减少间距以使按钮更紧凑 */
    width: auto; /* 自动宽度 */
    
    .user-section {
      margin-right: 0;
      
      .user-info {
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      height: 44px;
        width: 44px;
        cursor: pointer;
        
        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
        }
      }
    }
    
    .notification-icon {
      margin: 0;
      
      .notification-btn {
        padding: 4px;
      height: 44px;
        width: 44px;
        
        :deep(.el-icon) {
          font-size: 24px;
        }
      }
    }
  }
  
  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px; /* 增加高度 */
    width: 44px; /* 增加宽度 */
    padding: 0;
    font-size: 24px;
    cursor: pointer;
    touch-action: manipulation; /* 提高触摸设备的响应性 */
    
    .menu-icon-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    :deep(.el-icon) {
      font-size: 24px;
    }
  }
}

/* 确保移动端菜单在v-if=true时显示 */
.mobile-menu-visible {
  display: block !important;
  animation: slide-down 0.3s ease-out;
}

/* 滑入动画 */
@keyframes slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
  }
  
  .mobile-menu {
    display: none;
  position: fixed;
  top: 60px;
    left: 0;
  width: 100%;
  background: white;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 16px 16px;
    overflow: hidden;
    
    .mobile-nav-item {
      display: block;
    padding: 15px 20px;
    font-size: 16px;
    color: #333;
      border-bottom: 1px solid #f0f0f0;
      
      &:hover, &.router-link-active {
      background-color: #f5f7fa;
      }
    }
    
    .mobile-auth-buttons {
      display: flex;
    gap: 12px;
    padding: 15px 20px;
      
      .el-button {
        flex: 1;
    }
  }
}

// 主内容区域
.main-content {
  flex: 1;
  padding: 30px 0;
}

// 页脚样式
.footer {
  background-color: #333;
  color: #fff;
  padding: 20px 0;
  
  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    text-align: center;
    
    .copyright {
      font-size: 14px;
      color: #999;
      
      .beian-link {
        color: #999;
        text-decoration: none;
        margin-left: 5px;
        margin-right: 10px;
        &:hover {
          color: #fff;
        }
      }
    }
  }
}

// 页面过渡动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 全局卡片样式优化
.el-card {
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08) !important;
  }
  
  .el-card__header {
    padding: 18px 20px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .el-card__body {
    padding: 20px;
  }
}

// 全局按钮样式优化
.el-button {
  border-radius: 10px !important;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &.is-round {
    border-radius: 20px !important;
  }
  
  &--primary {
    background-color: #333 !important;
    border-color: #333 !important;
    
    &:hover, &:focus {
      background-color: #222 !important;
      border-color: #222 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &.is-plain {
      color: #333 !important;
      background-color: #fff !important;
      border-color: #aaa !important;
      
      &:hover, &:focus {
        color: #222 !important;
        border-color: #777 !important;
      }
    }
  }
  
  &--success {
    background-color: #444 !important;
    border-color: #444 !important;
    
    &:hover, &:focus {
      background-color: #333 !important;
      border-color: #333 !important;
    }
  }
}

// 全局输入框样式优化
.el-input__inner, .el-textarea__inner {
  border-radius: 10px !important;
}

// 全局标签样式优化
.el-tag {
  border-radius: 8px !important;
  
  &--success {
    background-color: #f0f0f0 !important;
    border-color: #ddd !important;
    color: #444 !important;
  }
  
  &--warning {
    background-color: #f5f5f5 !important;
    border-color: #ddd !important;
    color: #555 !important;
  }
  
  &--danger {
    background-color: #eee !important;
    border-color: #ddd !important;
    color: #444 !important;
  }
  
  &--primary {
    background-color: #e8e8e8 !important;
    border-color: #ddd !important;
    color: #333 !important;
  }
}

// 响应式调整
@media (max-width: 768px) {
  html, body {
    overflow-x: hidden !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background-color: #f5f5f7 !important;
    min-height: 100% !important;
  }
  
  // 移动端标题字体大小调整
  html body h1, 
  html body .h1,
  html body .main-title,
  html body .section-title,
  html body .page-title {
    font-size: 1.3rem !important;
    line-height: 1.3 !important;
  }
  
  html body h2,
  html body .h2,
  html body .sub-title,
  html body .card-title,
  html body .dashboard-title,
  html body .timeline-header span,
  html body .chart-header span {
    font-size: 1.1rem !important;
    line-height: 1.3 !important;
    font-weight: 600 !important;
  }
  
  html body h3, 
  html body .h3,
  html body .star-header h4,
  html body .section-title,
  html body .suggestions-title,
  html body .history-card-header h3 {
    font-size: 1rem !important;
    line-height: 1.3 !important;
    font-weight: 500 !important;
  }
  
  html body h4,
  html body .h4,
  html body .position-title,
  html body .stat-header span {
    font-size: 0.95rem !important;
    line-height: 1.3 !important;
  }
  
  html body h5, 
  html body h6,
  html body .h5,
  html body .h6 {
    font-size: 0.9rem !important;
    line-height: 1.3 !important;
  }
  
  // 确保所有卡片标题也使用合适的大小
  html body .el-card__header {
    font-size: 1.1rem !important;
  }
  
  // 资源管理仪表盘特定样式
  html body .stat-number {
    font-size: 1.5rem !important;
    line-height: 1.2 !important;
  }
  
  html body .stat-trend,
  html body .stat-subtitle {
    font-size: 0.8rem !important;
  }
  
  // 简历优化页面特定样式
  html body .input-guide {
    font-size: 0.85rem !important;
    line-height: 1.3 !important;
  }
  
  html body .page-desc {
    font-size: 0.9rem !important;
    line-height: 1.3 !important;
  }
  
  // 移动端卡片内容间距优化
  html body .el-card.stat-card .stat-content {
    padding: 5px 0 !important;
  }
  
  html body .dashboard-grid .stat-cards {
    margin-bottom: 15px !important;
  }
  
  html body .dashboard-grid .chart-row {
    margin-bottom: 15px !important;
  }
  
  html body .dashboard-grid .el-timeline {
    padding: 10px !important;
  }
  
  html body .star-input-block {
    margin-bottom: 15px !important;
  }
  
  html body .input-section .star-header {
    margin-bottom: 5px !important;
  }
  
  // 确保按钮在移动端有合适大小
  html body .action-buttons .el-button {
    padding: 8px 12px !important;
    font-size: 0.9rem !important;
  }
  
  #app {
    width: 100% !important;
    overflow-x: hidden !important;
    min-height: 100vh !important;
    background-color: #f5f5f7 !important;
  }
  
  .el-header.header {
    height: 60px !important;
    padding: 0 !important;
  }
  
  .header {
    height: 60px;
    
    .header-container {
      width: 100%;
      padding-left: 7%;   /* 左侧边距 */
      padding-right: 7%;   /* 右侧边距 */
    }
    
    .desktop-menu, .auth-buttons {
      display: none;
    }
    
    .right-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 5%; /* 设置固定间距 */
      width: auto; /* 自动宽度 */
      
      .user-section {
        margin-right: 0;
        
        .user-info {
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 40px;
          width: 40px;
          cursor: pointer;
          
          .user-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
          }
        }
      }
      
      .notification-icon {
        margin: 0;
        
        .notification-btn {
          padding: 8px;
          height: 40px;
          width: 40px;
          
          :deep(.el-icon) {
            font-size: 22px;
          }
        }
      }
    }
    
    .mobile-menu-btn {
      display: flex; /* 只使用一次display:flex */
      align-items: center;
      justify-content: center;
      height: 40px;
      width: 40px;
      padding: 0;
      font-size: 22px;
      cursor: pointer;
    }
    
    .logo h1 {
      font-size: 1.5rem;
      padding: 8px 0;
    }
  }
  
  // 用户菜单修复
  .user-section {
    .user-info {
      padding: 8px;
    }
    
    .user-tooltip {
      position: fixed;
      top: 60px;
      right: 0;
      width: 100%;
      max-width: 320px;
      border-radius: 0 0 12px 12px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
  
  .footer {
    padding: 15px 0;
    
    .footer-content {
      width: 100%;
      padding: 0 15px;
      
      .copyright {
        font-size: 12px;
      }
    }
  }
  
  .main-content {
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    background-color: #fff !important;
  }
  
  // 移动端样式强制覆盖 - 专门修复白色卡片填充问题
  html body .el-card {
    border-radius: 12px !important;
    margin: 10px !important;
    padding: 0 !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
    border: none !important;
    width: calc(100% - 20px) !important;
    background-color: #fff !important;
    
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
  }
  
  html body .el-card.el-card--small {
    border-radius: 8px !important;
    margin-bottom: 10px !important;
  }
  
  html body .el-card .el-card__body {
    padding: 15px !important;
    width: 100% !important;
    background-color: #fff !important;
  }
  
  html body .resume-view {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background-color: #f5f5f7 !important;
  }
  
  html body .resume-view .main-card {
    margin: 10px !important;
    padding: 0 !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
    width: calc(100% - 20px) !important;
    background-color: #fff !important;
    border: none !important;
    overflow: hidden !important;
  }
  
  html body .resume-view .history-container .history-card {
    margin: 0 0 15px 0 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  html body .el-tabs__content {
    padding: 0 !important;
    background-color: #fff !important;
  }
  
  html body .el-tabs__header {
    margin: 0 !important;
    padding: 0 15px !important;
    background-color: #fff !important;
  }
  
  html body .el-tab-pane {
    padding: 0 !important;
    background-color: #fff !important;
  }
  
  html body .el-main {
    padding: 0 !important;
    width: 100% !important;
    background-color: #f5f5f7 !important;
  }
  
  // 全局表单项样式优化
  body .el-form-item {
    margin-bottom: 16px !important;
    }
  
  body .el-form-item__label {
    padding: 0 0 8px !important;
    line-height: 1.4 !important;
    text-align: left !important;
  }
  
  body .el-input__wrapper,
  body .el-textarea__wrapper {
    width: 100% !important;
  }
  
  body .el-input__inner {
    height: 42px !important;
  }
  
  body .el-button {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }
  
  body .el-card.custom-card {
    border-radius: 12px !important;
    margin: 10px !important;
  }
  
  body .el-card .el-card__header {
    padding: 15px !important;
  }
  
  body .el-dialog {
    width: 92% !important;
    max-width: 420px !important;
    margin: 0 auto !important;
  }
  
  body .el-dialog .el-dialog__body {
    padding: 16px !important;
  }
  
  // 调整表格在移动端的显示
  body .el-table {
    width: 100% !important;
    overflow-x: auto !important;
  }
  
  body .el-table th,
  body .el-table td {
    padding: 8px !important;
  }
  
  // Tab样式优化
  body .el-tabs__item {
    padding: 0 16px !important;
  }
  
  // 搜索框优化
  body .el-input-group__append .el-button {
    padding: 10px !important;
  }
  
  // 分页组件优化
  body .el-pagination {
    justify-content: center !important;
    padding: 0 !important;
    margin: 10px 0 !important;
  }
  
  body .el-pagination .el-pagination__sizes {
    display: none !important;
  }
  
  // 优化卡片标题、表单和列表项的间距
  html body .el-card__header {
    padding: 12px 15px !important;
  }
  
  html body .el-form-item {
    margin-bottom: 12px !important;
  }
  
  html body .el-form-item__label {
    padding-bottom: 5px !important;
    font-size: 0.9rem !important;
  }
  
  html body .el-form-item__content {
    line-height: 1.3 !important;
  }
  
  html body .el-input__inner {
    height: 36px !important;
    font-size: 0.9rem !important;
  }
  
  html body .el-textarea__inner {
    font-size: 0.9rem !important;
    line-height: 1.5 !important;
  }
  
  html body .el-timeline-item__content {
    font-size: 0.9rem !important;
  }
  
  html body .el-timeline-item__timestamp {
    font-size: 0.8rem !important;
  }
  
  // 确保移动端的表格内容也更紧凑
  html body .el-table {
    font-size: 0.9rem !important;
  }
  
  html body .el-table th,
  html body .el-table td {
    padding: 6px 8px !important;
  }
  
  // 通用文本元素样式优化
  html body p,
  html body .text,
  html body .description,
  html body .content p,
  html body .card-content p,
  html body li:not(.el-dropdown-menu__item):not(.el-menu-item):not(.el-cascader-node__label),
  html body .info-text,
  html body .text-content,
  html body .plain-text {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }
  
  // 针对常见文本元素
  html body .info-item .label,
  html body .info-item .value,
  html body .detail-item .label, 
  html body .detail-item .value,
  html body .user-tooltip .tooltip-details .detail-item .value,
  html body .card-desc,
  html body .notification-content,
  html body .timeline-item,
  html body .info-text {
    font-size: 0.9rem !important;
  }
  
  // 描述性文字专门优化
  html body .page-description,
  html body .page-desc,
  html body .sub-title,
  html body .desc-paragraph,
  html body .contact-desc,
  html body .input-guide,
  html body .card-desc,
  html body .description,
  html body .setting-header .description,
  html body .faq-answer,
  html body .subtitle {
    font-size: 0.85rem !important;
    line-height: 1.4 !important;
    margin-bottom: 10px !important;
  }
  
  // 表单相关文本
  html body .el-form-item__label,
  html body .el-checkbox__label,
  html body .el-radio__label,
  html body label {
    font-size: 0.85rem !important;
    line-height: 1.3 !important;
  }
  
  // 用户资料页面特定样式
  html body .user-profile {
    .profile-card {
      .profile-header {
        display: flex !important;
        flex-direction: row !important;
        padding: 15px !important;
        align-items: flex-start !important;
        justify-content: space-between !important;
        
        .avatar-section {
          min-width: auto !important;
          width: 35% !important;
          margin-right: 10px !important;
          text-align: center !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          
          .user-avatar {
            width: 60px !important;
            height: 60px !important;
            border-radius: 30px !important;
          }
          
          .user-name-wrapper h2 {
            font-size: 0.9rem !important;
            margin: 3px 0 2px !important;
          }
          
          .upload-btn {
            width: 60px !important;
            font-size: 0.7rem !important;
            padding: 2px 0 !important;
            height: 22px !important;
            line-height: 1 !important;
          }
        }
        
        .info-section {
          margin-left: 0 !important;
          width: 65% !important;
          padding-left: 5px !important;
          
          .info-columns {
            display: block !important;
            grid-template-columns: none !important;
            grid-template-rows: none !important;
            gap: 5px !important;
            
            .info-group {
              margin-bottom: 5px !important;
            }
            
            .info-item {
              margin-bottom: 4px !important;
              flex-wrap: wrap !important;
              
              .label {
                width: 60px !important;
                min-width: 60px !important;
                font-size: 0.75rem !important;
              }
              
              .value {
                flex-grow: 1 !important;
                min-width: 0 !important;
                font-size: 0.75rem !important;
                word-break: break-all !important;
                
                .el-tag {
                  font-size: 0.7rem !important;
                  padding: 0 4px !important;
                  height: 18px !important;
                  line-height: 18px !important;
                }
                
                .upgrade-btn {
                  margin-left: 4px !important;
                  padding: 0 4px !important;
                  font-size: 0.7rem !important;
                  height: 18px !important;
                }
              }
            }
          }
        }
      }
      
      .settings-content {
        padding: 10px !important;
        
        .settings-item {
          padding: 8px 0 !important;
          
          .setting-header {
            padding-right: 5px !important;
            
            .label {
              font-size: 0.8rem !important;
              margin-bottom: 0 !important;
            }
            
            .description {
              font-size: 0.7rem !important;
            }
          }
          
          .setting-control {
            width: 70px !important;
            
            .theme-switch,
            .notification-switch {
              transform: scale(0.7) !important;
            }
            
            .el-button {
              font-size: 0.7rem !important;
              padding: 2px 6px !important;
              height: auto !important;
            }
          }
        }
      }
    }
    
    // 减少页面间距
    .section-divider {
      margin: 10px 0 !important;
    }
    
    .page-title {
      margin: 6px auto !important;
      padding: 8px !important;
      font-size: 1.1rem !important;
    }
  }
  
  // 修复资源管理页公司卡片样式问题
  html body .resources-home-view {
    .company-list-container {
      width: 100% !important;
    }
    
    // 表单搜索栏优化
    .header-actions {
      flex-direction: column !important;
      gap: 10px !important;
      padding-bottom: 10px !important;
      
      .search-box {
        width: 100% !important;
      }
      
      .resource-actions {
        width: 100% !important;
        text-align: center !important;
      }
    }
    
    // 仪表盘视图优化
    .dashboard-container {
      width: 100% !important;
    }
    
    .company-list {
      padding: 0 10px !important;
      
      .company-item {
        margin-bottom: 10px !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
        overflow: visible !important; // 修改为可见，防止头像被截断
        padding: 15px !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        
        .company-main {
          width: 100% !important;
          padding-right: 0 !important;
          margin-bottom: 5px !important; // 减少底部间距
          
          .company-info {
            .company-logo {
              width: 36px !important; // 减小头像尺寸
              height: 36px !important;
              position: relative !important; // 添加定位
              z-index: 2 !important; // 提高层级
              
              .el-avatar {
                width: 36px !important;
                height: 36px !important;
                font-size: 16px !important;
              }
            }
            
            .company-details {
              .company-name {
                font-size: 0.95rem !important;
                margin-bottom: 2px !important; // 减少间距
              }
              
              .company-meta {
                font-size: 0.75rem !important; // 减小字体
                gap: 8px !important; // 减少间距
                
                .company-location, .company-industry {
                  font-size: 0.75rem !important;
                  gap: 2px !important;
                  
                  .el-icon {
                    font-size: 0.8rem !important; // 减小图标
                  }
                }
              }
            }
          }
        }
        
        .company-actions {
          width: 100% !important;
          flex-direction: column !important;
          gap: 5px !important; // 减少间距
          
          .status-tags {
            width: 100% !important;
            justify-content: space-between !important;
            
            .el-tag {
              padding: 0 6px !important;
              height: 20px !important;
              font-size: 0.7rem !important;
              line-height: 20px !important;
            }
            
            .interview-status-badge {
              .el-badge__content {
                padding: 0 6px !important;
                font-size: 0.7rem !important;
              }
            }
          }
          
          .interaction-icons {
            width: 100% !important;
            justify-content: space-between !important;
            padding: 5px 0 0 0 !important; // 减少上边距
            border-top: 1px solid #f0f0f0 !important;
            margin-top: 3px !important;
            
            .icon-item {
              min-width: auto !important; // 清除最小宽度
              padding: 0 5px !important;
              
              .custom-icon {
                font-size: 0.9rem !important;
                padding: 8px !important;
                width: 24px !important;
                height: 24px !important;
              }
              
              .icon-label {
                font-size: 0.7rem !important;
                margin-top: 4px !important;
              }
            }
          }
        }
      }
    }
    
    // 修复仪表盘卡片间距问题
    .dashboard-grid {
      .chart-row {
        margin: 0 !important; // 移除外边距
        padding: 0 !important; // 修改为0内边距
        box-sizing: border-box !important;
        
        .el-col {
          width: 100% !important;
          max-width: 100% !important;
          flex: 0 0 100% !important;
          padding: 0 !important; // 移除内边距
          margin: 0 0 10px 0 !important; // 只保留底部间距
          box-sizing: border-box !important;
        }
        
        .chart-card {
          border-radius: 12px !important;
          overflow: hidden !important;
          margin: 0 10px !important; // 添加左右10px间距，与时间线卡片保持一致
          width: calc(100% - 20px) !important; // 计算宽度减去两边边距
          box-sizing: border-box !important;
          
          .el-card__header {
            padding: 8px 10px !important;
          }
          
          .chart-content {
            padding: 5px !important;
            width: 100% !important;
            box-sizing: border-box !important;
            
            div[ref="trendChartRef"],
            div[ref="statusChartRef"],
            div {
              height: 200px !important;
              width: 100% !important; // 确保图表宽度100%
              box-sizing: border-box !important;
              margin: 0 auto !important; // 居中显示
            }
          }
        }
      }
      
      .stat-cards {
        margin: 0 !important; // 移除原有外边距
        padding: 0 !important; // 移除内边距
        display: flex !important;
        flex-wrap: wrap !important;
        
        .el-col {
          width: 50% !important;
          max-width: 50% !important;
          flex: 0 0 50% !important;
          padding: 0 !important; // 移除内边距
          margin-bottom: 10px !important;
        }
        
        .stat-card {
          border-radius: 12px !important;
          overflow: hidden !important;
          height: 100% !important;
          margin: 0 5px !important; // 添加5px左右边距，确保两个卡片间有10px间距
          width: calc(100% - 10px) !important; // 计算实际宽度
          
          .el-card__header {
            padding: 8px 10px !important;
            
            .stat-header {
              .el-icon {
                font-size: 0.9rem !important;
                margin-right: 4px !important;
              }
              
              span {
                font-size: 0.8rem !important;
              }
            }
          }
          
          .stat-content {
            padding: 8px !important;
            
            .stat-number {
              font-size: 1.3rem !important;
              margin-bottom: 4px !important;
            }
            
            .stat-trend,
            .stat-subtitle {
              font-size: 0.7rem !important;
            }
          }
        }
      }
      
      // 确保四个统计卡片在一行内的间距正确
      .stat-cards .el-col:nth-child(odd) .stat-card {
        margin-left: 10px !important; // 左侧卡片左边距为10px，与其他卡片一致
        margin-right: 5px !important; // 右边距5px，与右侧卡片间距为10px
      }
      
      .stat-cards .el-col:nth-child(even) .stat-card {
        margin-left: 5px !important; // 左边距5px，与左侧卡片间距为10px
        margin-right: 10px !important; // 右侧卡片右边距为10px，与其他卡片一致
      }
      
      // 添加时间线卡片样式
      .timeline-card {
        border-radius: 12px !important;
        overflow: hidden !important;
        margin: 0 10px 10px 10px !important; // 添加左右10px间距，确保与其他卡片一致
        width: calc(100% - 20px) !important; // 计算宽度减去两边边距
        box-sizing: border-box !important;
        
        .el-card__header {
          padding: 8px 10px !important;
        }
        
        .el-timeline {
          padding: 10px 5px !important;
          
          .el-timeline-item__node {
            width: 24px !important;
            height: 24px !important;
          }
          
          .el-timeline-item__wrapper {
            padding-left: 36px !important;
          }
          
          .el-timeline-item__timestamp {
            font-size: 0.75rem !important;
          }
          
          .el-timeline-item__content {
            font-size: 0.8rem !important;
          }
        }
      }
    }
  }
  
  // 首页卡片样式优化
  html body .home {
    width: 100% !important;
    padding: 0 10px !important; // 添加内边距
    box-sizing: border-box !important;
    
    .feature-cards {
      margin-bottom: 30px !important;
      box-sizing: border-box !important;
      width: 100% !important;
      
      .el-row {
        display: flex !important;
        flex-wrap: wrap !important;
        margin: 0 -5px !important; // 使用负边距抵消列的内边距
        width: 100% !important;
        box-sizing: border-box !important;
      }
      
      .el-col {
        width: 50% !important;
        max-width: 50% !important;
        flex: 0 0 50% !important;
        margin-bottom: 10px !important;
        padding: 0 5px !important; // 确保左右各5px的内边距
        box-sizing: border-box !important;
      }
      
      .feature-card {
        margin: 0 !important;
        width: 100% !important;
        height: 100% !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
        box-sizing: border-box !important;
        
        &:hover {
          transform: none !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
        }
        
        .card-content {
          padding: 15px 10px !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .card-icon {
          font-size: 2rem !important;
          margin-bottom: 10px !important;
        }
        
        .card-title {
          margin-bottom: 8px !important;
        }
        
        .card-desc {
          margin-bottom: 15px !important;
          font-size: 0.8rem !important;
        }
        
        .card-btn {
          padding: 6px 12px !important;
          font-size: 0.8rem !important;
        }
      }
    }
    
    // 修复其他首页内容的左右间距
    .hero, .contact-section {
      width: 100% !important;
      padding-left: 10px !important;
      padding-right: 10px !important;
      box-sizing: border-box !important;
    }
  }
  
  // 弹窗文本样式
  html body .el-dialog__body {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }
  
  html body .el-message-box__content {
    font-size: 0.9rem !important;
    line-height: 1.4 !important;
  }
  
  // 导航菜单文字
  html body .mobile-nav-item {
    font-size: 1rem !important;
    padding: 10px 20px !important;
  }
  
  // 优化表单元素在移动端的样式
  html body .el-form {
    .el-form-item {
      margin-bottom: 12px !important;
    }
    
    .el-select {
      width: 100% !important;
    }
  }
  
  html body .el-dialog {
    width: 90% !important;
    max-width: 90% !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    
    .el-dialog__header {
      padding: 12px 15px !important;
    }
    
    .el-dialog__body {
      padding: 15px !important;
    }
    
    .el-dialog__footer {
      padding: 10px 15px !important;
    }
  }
  
  // 通用表格和分页优化
  html body .el-table__header th {
    padding: 6px 0 !important;
    font-size: 0.85rem !important;
  }
  
  html body .el-pagination {
    justify-content: center !important;
    padding-top: 10px !important;
    
    .btn-prev,
    .btn-next,
    .el-pager li {
      min-width: 28px !important;
      height: 28px !important;
      line-height: 28px !important;
      font-size: 0.85rem !important;
    }
  }
}

.user-section {
  position: relative;
  
  .user-info {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 2px;
    border-radius: 50%;
    
    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
    }
    
    .avatar-vip-badge {
      position: absolute;
      bottom: -2px;
      right: -2px;
      transform: scale(0.8);
      z-index: 1;
    }
  }
  
  .user-tooltip {
    position: absolute;
    top: 45px;
    right: 0;
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    padding: 16px;
    min-width: 280px;
    z-index: 1000;
    
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      right: 0;
      left: 0;
      height: 10px;
      background: transparent;
    }

    .tooltip-content {
      cursor: pointer;
    }
    
    .tooltip-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 12px;
      
      .tooltip-avatar {
        width: 48px;
        height: 48px;
        border-radius: 24px;
        object-fit: cover;
      }
      
      .tooltip-user-info {
        flex: 1;
        
        .tooltip-name-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          
          .tooltip-name {
            font-size: 16px;
            font-weight: 600;
            color: #333;
          }
        }
      }
    }
    
    .tooltip-details {
      .detail-item {
        display: flex;
        margin: 8px 0;
        font-size: 14px;
        
        .label {
          color: #666;
          width: 50px;
          flex-shrink: 0;
        }
        
        .value {
          color: #333;
          flex: 1;
          word-break: break-all;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
    
    .tooltip-actions {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 0;
      
      .action-btn {
        display: block;
        text-align: left;
        padding: 8px 16px;
        width: 100%;
        height: auto;
        transition: all 0.3s ease;
        border-radius: 6px;
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
        border: none;
        background: transparent;
        cursor: pointer;
        
        &:hover {
          background-color: #f5f7fa;
        }
        
        &.logout-btn {
          color: #f56c6c;
          
          &:hover {
            background-color: #fef0f0;
          }
        }
      }
    }
  }
}

.user-info-popup {
  padding: 12px;
  min-width: 200px;
}

.user-info-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 8px;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.username-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.username {
  font-weight: 500;
  color: #333;
}

.email {
  font-size: 12px;
  color: #666;
}

.mini-badge {
  transform: scale(0.8);
}
</style>
